export const WALL_DECOR_OUTPUT_LAYOUT = "wall-decor-sheet";

export const WALL_DECOR_PRODUCTION_DEFAULTS = {
  printBackground: "transparent",
  cutContourName: "CutContour",
  cutStrokePt: 0.25,
  // The printable artwork contains 1.5 mm colour bleed outside the approved
  // contour. Cutting at the mask edge prevents a visible white vinyl halo.
  cutOffsetMm: 0,
  printBleedMm: 1.5,
  cutOffsetReferenceWidthMm: 900,
  cutJoinMm: 0,
  cutJoinReferenceWidthMm: 900,
  alphaThreshold: 16,
  minElementAreaMm2: 12,
  traceWidthPx: 1024,
  simplifyPx: 1.15,
  maxCutContours: 160,
};

export const WALL_DECOR_SIZES = [
  {
    value: "small",
    label: "Liten",
    description: "Et lite blikkfang ved seng, hylle eller lekekrok",
    widthMm: 550,
    heightMm: 367,
    spreadLabel: "ca. 75 × 50 cm ferdig på veggen",
    printAreaSqm: 0.2,
    priceDeltaNok: 0,
  },
  {
    value: "medium",
    label: "Medium",
    description: "Vår mest populære størrelse til én veggsone",
    widthMm: 900,
    heightMm: 600,
    spreadLabel: "ca. 120 × 80 cm ferdig på veggen",
    printAreaSqm: 0.54,
    priceDeltaNok: 150,
    popular: true,
  },
  {
    value: "large",
    label: "Stor",
    description: "En tydelig motivverden over seng eller kommode",
    widthMm: 1250,
    heightMm: 833,
    spreadLabel: "ca. 165 × 110 cm ferdig på veggen",
    printAreaSqm: 1.04,
    priceDeltaNok: 350,
  },
  {
    value: "xl",
    label: "XL",
    description: "For en hel fondvegg eller et stort lekeområde",
    widthMm: 1500,
    heightMm: 1000,
    spreadLabel: "ca. 200 × 135 cm ferdig på veggen",
    printAreaSqm: 1.5,
    priceDeltaNok: 600,
  },
];

const WALL_DECOR_PREVIEW_MAX_WIDTH_MM = 1500;
const WALL_DECOR_PREVIEW_MAX_HEIGHT_MM = 1000;

export function wallDecorPreviewScale(size = {}) {
  const widthMm = Math.max(0, Number(size.widthMm) || 0);
  const heightMm = Math.max(0, Number(size.heightMm) || 0);
  // Tightly cropped artwork still needs to show clear physical size steps,
  // but even the smallest set must be large enough to judge on a screen.
  const widthPct = Math.min(88, Math.max(48, 38 + widthMm / WALL_DECOR_PREVIEW_MAX_WIDTH_MM * 50));
  const heightPct = Math.min(72, Math.max(38, 30 + heightMm / WALL_DECOR_PREVIEW_MAX_HEIGHT_MM * 42));
  return {
    widthPct: Number(widthPct.toFixed(2)),
    heightPct: Number(heightPct.toFixed(2)),
  };
}

export const WALL_DECOR_THEMES = [
  theme("forest-friends", "Skogens små venner", "Dyr og natur", 14, "forest-friends", { roomImageUrl: "/wall-decor/hero-forest-room.webp", cutJoinMm: 1 }),
  theme("space-adventure", "Min egen verdensromferd", "Drømmer og himmel", 16, "space-adventure", { roomImageUrl: "/wall-decor/room-space-ceiling.webp", ceilingSuitable: true, cutJoinMm: 5.3 }),
  theme("ocean-friends", "Havets hemmelige venner", "Hav og dyr", 16, "ocean-friends"),
  theme("dinosaur-valley", "Dinosaurdalen", "Dinosaurer", 15, "dinosaur-valley", { cutJoinMm: 3.6 }),
  theme("construction-crew", "Byggegjengen", "Kjøretøy", 15, "construction-crew", { cutJoinMm: 2.7 }),
  theme("dream-clouds", "Drømmeskyer", "Drømmer og himmel", 18, "dream-clouds", { ceilingSuitable: true }),
  theme("flower-meadow", "Blomstereng", "Dyr og natur", 20, "flower-meadow"),
  theme("northern-lights-dream", "Nordlysdrøm", "Drømmer og himmel", 13, "northern-lights-dream", { ceilingSuitable: true, cutJoinMm: 1 }),
  theme("secret-forest-cabin", "Den hemmelige skogshytta", "Dyr og natur", 11, "secret-forest-cabin", { cutJoinMm: 1 }),
  theme("rainbow-safari", "Regnbuesafari", "Dyr og natur", 12, "rainbow-safari"),
  theme("balloon-cloud-journey", "Ballongferd over skyene", "Drømmer og himmel", 11, "balloon-cloud-journey", { ceilingSuitable: true }),
  theme("robot-workshop", "Robotverkstedet", "Lek og aktivitet", 10, "robot-workshop"),
  theme("bee-flower-garden", "Bienes blomsterhage", "Dyr og natur", 12, "bee-flower-garden", { cutJoinMm: 3.6 }),
  theme("arctic-friends", "Arktiske venner", "Dyr og natur", 11, "arctic-friends"),
  theme("moon-rabbits", "Månekaninenes natt", "Drømmer og himmel", 13, "moon-rabbits", { ceilingSuitable: true }),
  theme("magic-fairy-garden", "Den magiske fehagen", "Eventyr", 12, "magic-fairy-garden"),
  theme("woodland-train", "Skogstoget", "Kjøretøy", 9, "woodland-train"),
  theme("starry-ceiling", "Stjernehimmel i taket", "Drømmer og himmel", 27, "starry-ceiling", { ceilingSuitable: true }),
  theme("rainbow-clouds", "Regnbueglede", "Drømmer og himmel", 14, "rainbow-clouds", { ceilingSuitable: true, cutJoinMm: 1 }),
  theme("playtime-friends", "Lekende venner", "Lek og aktivitet", 8, "playtime-friends", { cutJoinMm: 1 }),
  theme("doll-tea-party", "Dukkenes teselskap", "Lek og aktivitet", 10, "doll-tea-party"),
  theme("sports-playground", "Min sportsplass", "Lek og aktivitet", 9, "sports-playground"),
  theme("butterfly-dream", "Sommerfugldrøm", "Dyr og natur", 14, "butterfly-dream", { ceilingSuitable: true }),
  theme("sleepy-bear-moon", "Bamsens månenatt", "Drømmer og himmel", 14, "sleepy-bear-moon", { ceilingSuitable: true, cutJoinMm: 1 }),
  theme("swan-cloud-dream", "Svaner over skyene", "Drømmer og himmel", 14, "swan-cloud-dream", { ceilingSuitable: true, cutJoinMm: 1 }),
  theme("little-fox-goodnight", "Lille rev sier god natt", "Drømmer og himmel", 13, "little-fox-goodnight", { ceilingSuitable: true }),
  theme("rainbow-castle", "Regnbueslottet", "Eventyr", 12, "rainbow-castle", { ceilingSuitable: true, cutJoinMm: 1 }),
  theme("music-room-friends", "Musikkfest på rommet", "Lek og aktivitet", 12, "music-room-friends"),
  theme("building-block-town", "Byggeklossbyen", "Lek og aktivitet", 12, "building-block-town", { cutJoinMm: 1 }),
];

export function resolveWallDecorSize(template, designOrResponses = {}) {
  const options = template?.sizeOptions?.length ? template.sizeOptions : WALL_DECOR_SIZES;
  const responses = designOrResponses.responses || designOrResponses;
  const field = (template?.fields || []).find((item) => item.role === "wallDecorSize");
  const value = responses?.[field?.id] || field?.defaultValue || template?.defaultSizeValue || "medium";
  return options.find((item) => item.value === value) || options[0];
}

export function resolveWallDecorTheme(template, designOrResponses = {}) {
  const responses = designOrResponses.responses || designOrResponses;
  const field = (template?.fields || []).find((item) => item.role === "wallDecorTheme" || item.role === "labelTheme");
  const value = responses?.[field?.id] || field?.defaultValue;
  return field?.options?.find((item) => item.value === value) || field?.options?.[0] || null;
}

export function resolveWallDecorProductionConfig(template) {
  const current = template?.wallDecorProduction || {};
  return {
    ...WALL_DECOR_PRODUCTION_DEFAULTS,
    ...current,
    printBackground: current.printBackground === "white" ? "white" : "transparent",
    cutContourName: String(current.cutContourName || WALL_DECOR_PRODUCTION_DEFAULTS.cutContourName).trim() || "CutContour",
    cutStrokePt: Math.max(0.05, Math.min(2, Number(current.cutStrokePt) || WALL_DECOR_PRODUCTION_DEFAULTS.cutStrokePt)),
    cutOffsetMm: Math.max(0, Math.min(8, Number(current.cutOffsetMm) || 0)),
    cutJoinMm: Math.max(0, Math.min(12, Number(current.cutJoinMm) || 0)),
    cutOffsetReferenceWidthMm: Math.max(100, Math.min(3000, Number(current.cutOffsetReferenceWidthMm) || WALL_DECOR_PRODUCTION_DEFAULTS.cutOffsetReferenceWidthMm)),
    cutJoinReferenceWidthMm: Math.max(100, Math.min(3000, Number(current.cutJoinReferenceWidthMm) || WALL_DECOR_PRODUCTION_DEFAULTS.cutJoinReferenceWidthMm)),
    alphaThreshold: Math.max(1, Math.min(254, Math.round(Number(current.alphaThreshold) || WALL_DECOR_PRODUCTION_DEFAULTS.alphaThreshold))),
    minElementAreaMm2: Math.max(1, Number(current.minElementAreaMm2) || WALL_DECOR_PRODUCTION_DEFAULTS.minElementAreaMm2),
    traceWidthPx: Math.max(384, Math.min(1400, Math.round(Number(current.traceWidthPx) || WALL_DECOR_PRODUCTION_DEFAULTS.traceWidthPx))),
    simplifyPx: Math.max(0.2, Math.min(5, Number(current.simplifyPx) || WALL_DECOR_PRODUCTION_DEFAULTS.simplifyPx)),
    maxCutContours: Math.max(1, Math.min(500, Math.round(Number(current.maxCutContours) || WALL_DECOR_PRODUCTION_DEFAULTS.maxCutContours))),
  };
}

export function validateWallDecorTemplate(template) {
  if (template?.outputLayout !== WALL_DECOR_OUTPUT_LAYOUT) return [];
  const issues = [];
  const sizes = template.sizeOptions || [];
  const themes = (template.fields || []).find((field) => field.role === "wallDecorTheme")?.options || [];
  if (!sizes.length) issues.push(issue("sizes_missing", "Legg til minst én størrelse."));
  if (!themes.length) issues.push(issue("themes_missing", "Legg til minst ett veggdekormotiv."));
  for (const size of sizes) {
    if (!size.value || !size.label || Number(size.widthMm) <= 0 || Number(size.heightMm) <= 0) issues.push(issue("size_invalid", `Størrelsen ${size.label || size.value || "uten navn"} er ugyldig.`));
    if (Number(size.widthMm) > 1610) issues.push(issue("size_too_wide", `${size.label} er bredere enn printerens maksimale printbredde på 1610 mm.`));
  }
  const values = new Set();
  for (const motif of themes) {
    if (!motif.value || values.has(motif.value)) issues.push(issue("theme_id_invalid", `Motivet ${motif.label || "uten navn"} mangler en unik ID.`));
    values.add(motif.value);
    if (!motif.label) issues.push(issue("theme_name_missing", "Et motiv mangler navn."));
    if (!motif.thumbnailUrl && !motif.imageUrl) issues.push(issue("theme_preview_missing", `${motif.label || "Motivet"} mangler forhåndsvisning.`));
    if (!motif.productionAssetUrl) issues.push(issue("theme_production_missing", `${motif.label || "Motivet"} mangler produksjonsfil.`));
    if (motif.productionHasTransparency !== true) issues.push(issue("theme_transparency_missing", `${motif.label || "Motivet"} må ha transparent produksjonsbakgrunn for individuelle skjærebaner.`));
    if (motif.commercialRightsConfirmed !== true) issues.push(issue("theme_rights_missing", `Bekreft kommersielle rettigheter for ${motif.label || "motivet"}.`));
  }
  return issues;
}

function theme(value, label, category, elementCount, filename, extras = {}) {
  return {
    value,
    label,
    category,
    elementCount,
    imageUrl: `/wall-decor/${filename}-thumb-v2.webp`,
    thumbnailUrl: `/wall-decor/${filename}-thumb-v2.webp`,
    cutMaskAssetUrl: `/wall-decor/${filename}-cut-v2.png`,
    productionAssetUrl: `/wall-decor/${filename}-production-v2.webp`,
    productionWidth: 4096,
    productionHeight: 2731,
    productionHasTransparency: true,
    commercialRightsConfirmed: true,
    ...(filename === "woodland-train" ? { cutJoinMm: 3.6 } : {}),
    rightsNote: "Originalt motiv laget for Navnlapp.no – ingen lisensierte figurer eller varemerker.",
    ...extras,
  };
}

function issue(code, message) {
  return { code, message };
}
